/**
 * «Без автоправок»: сколько звучит озвучка реплики — мерка, по которой снимаются наложения.
 *
 * Правило владельца 01.10.2026: «ты должен всегда сдвигать соседние реплики иначе будут
 * наслоения». Снимают наложения трое — сервер редактора после озвучки, сборка и раздвижка
 * редактора после правки, — и мерить им положено одинаково, иначе автосохранение редактора
 * перезаписывает толчок сервера более ранним местом и наложение возвращается на диск.
 *
 * Мерка сервера — `dub_ms` в данных папки ручной пары (translate-python `dub_sounding_ms`):
 * длина самого wav реплики (из `file`, иначе `{id}_{lang}.wav`) в целых мс. json `length` для
 * этого не годится: у кусков разбиения и реплик «+» это слот, а не звук.
 *   * `dub_ms` — число: озвучена, звучит столько;
 *   * `dub_ms === null`: сервер реплику озвученной не считает (нет wav или снята с озвучки) —
 *     она не толкает и не толкается, даже если `file` у неё есть;
 *   * поля нет (старый сервер): прежнее правило — озвучена, если есть `file`, звучит `length`.
 * Снятая с озвучки реплика (`skip`) и озвучка без реплики оригинала (`orphan_voicing`) не
 * звучат никогда — что бы ни говорили `file` и `dub_ms` (см. `voicingSilenced`).
 */
export interface DubMeasured {
    file?: string | null;
    length: number;
    dub_ms?: number | null;
    skip?: boolean | null;
    orphan_voicing?: boolean | null;
}

/**
 * Озвучки, которой в видео нет, что бы ни лежало на записи: реплика снята с озвучки (⊘,
 * `skip: true`) или у озвучки нет реплики оригинала в папке (`orphan_voicing`, см.
 * orphanVoicings.ts: 4119e839, ru_es, папка 1, 1:22, «Así es.», 02.10.2026 — русскую реплику
 * удалили, испанский перевод и его wav остались, и редактор играл дубль, которого в видео быть не
 * должно). Одно правило на всех: расписание, границы перетаскивания, раздвижка соседей и паузы
 * озвучки спрашивают только его — через `voicingSounds` и `dubSoundingMs`.
 */
export const voicingSilenced = (track: { skip?: boolean | null; orphan_voicing?: boolean | null }): boolean =>
    track.skip === true || track.orphan_voicing === true;

/**
 * Звучит ли озвучка реплики — в видео и потому в редакторе: файл есть, и озвучка не заглушена
 * (`voicingSilenced`: не снята с озвучки ⊘, и реплика оригинала у неё есть).
 *
 * Новый сервер снимает реплику пометкой на той же записи и оставляет на ней старый `file` (чтобы
 * «вернуть» и ♪ ничего не потеряли), а сборка снятую не берёт. Раньше у записи-пропуска `file`
 * не было, и реплика молчала; с `file` редактор играл бы дубль, про который метка говорит «не
 * попадёт в видео», и толкал бы им соседей (тот же класс беды, что 01.10.2026, fdcde2a1, 2:10:
 * редактор звучал не так, как видео). Поэтому «есть file — звучит» решается только здесь.
 */
export const voicingSounds = (track: {
    file?: string | null;
    skip?: boolean | null;
    orphan_voicing?: boolean | null;
}): boolean => Boolean(track.file) && !voicingSilenced(track);

export function dubSoundingMs(track: DubMeasured): number | null {
    if (voicingSilenced(track)) {
        return null;
    }
    const measured = track.dub_ms;
    if (typeof measured === "number") {
        return Number.isFinite(measured) ? measured : null;
    }
    if (measured === null) {
        return null;
    }
    if (!track.file) {
        return null;
    }
    return typeof track.length === "number" && Number.isFinite(track.length) ? track.length : 0;
}

/**
 * Место озвучки в целых мс — так его сравнивают все три прохода (int(start) на сервере и в
 * сборке): дробная часть, которую иногда оставляет запись json, не решает, есть ли наложение.
 * Только для сравнения с концом предыдущего дубля — порядок ленты задаёт `tapeOrder`.
 */
export const wholeMs = (value: number): number => Math.trunc(value);

/**
 * Порядок ленты, по которому снимаются наложения, — как у прохода сервера (translate-python
 * `_voiced_dubs`: сортировка по (start, id)) и у сборки: по месту КАК ЕСТЬ, с долями мс, при
 * равенстве — по id. Две озвучки в одной целой миллисекунде (1000.2 и 1000.7) идут по долям:
 * упорядоченный по целым мс и id редактор толкал бы не ту, что сервер и сборка (ревью
 * 01.10.2026). Место — не число (NaN): решает id.
 */
export const tapeOrder = (a: { start: number; id?: string }, b: { start: number; id?: string }): number => {
    const byStart = a.start - b.start;
    if (byStart) return byStart;
    const left = String(a.id ?? "");
    const right = String(b.id ?? "");
    return left < right ? -1 : left > right ? 1 : 0;
};
